import React, { useState, useEffect } from "react";
import { FaTrashCan, FaPen, FaShare } from "react-icons/fa6";
import { formatDistanceToNow } from 'date-fns';
import QuestionsPopup from "./QuestionsPopup";
import { 
  getPriorityDMByIdAPI,
  createPaymentSession,
  verifyPayment
} from "../../../../../Service/user";
import { toast } from "react-hot-toast";
import { load } from "@cashfreepayments/cashfree-js";
import PurchasePopup from "../../../../Shared/PurchasePopup/PurchasePopup";
import './ServiceCards.scss';
import { useNavigate } from 'react-router-dom';
import SharePopup from "../../../../PopUp/SocialSharePopup/SharePopup";
const PriorityDMCard = ({
  communityId,
  dm,
  isAdmin,
  loggedInUserId,
  onDelete,
  onUpdate,
  onAsk
}) => {
  const [question, setQuestion] = useState("");
  const [showQuestionForm, setShowQuestionForm] = useState(false);
  const [showQuestions, setShowQuestions] = useState(false);
  const [questions, setQuestions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isAskLoading, setIsAskLoading] = useState(false);
  const [showUserQuestions, setShowUserQuestions] = useState(false);
  const [userQuestions, setUserQuestions] = useState([]);
  const [showUserQuestionsPopup, setShowUserQuestionsPopup] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [sharePopupOpen, setSharePopupOpen] = useState(false);
  const [socialUrl, setSocialUrl] = useState('');
  const navigate = useNavigate();
  const loggedInUser = JSON.parse(localStorage.getItem("loggedInUser"));
  useEffect(() => {    
    // Filter questions asked by the logged-in user
    if (dm.questions) {
      const userAskedQuestions = dm.questions.filter(q => {
        const qUserId = q.userId._id || q.userId;
        return qUserId === loggedInUserId;
      });
      setUserQuestions(userAskedQuestions);
    }
  }, [dm.questions, loggedInUserId]);

  const formatTimeline = (minutes) => {
    if (minutes < 60) {
      return `${minutes} minutes`;
    } else if (minutes < 1440) { // Less than 24 hours
      const hours = Math.floor(minutes / 60);
      return `${hours} ${hours === 1 ? 'hour' : 'hours'}`;
    } else { // Days
      const days = Math.floor(minutes / 1440);
      const remainingHours = Math.floor((minutes % 1440) / 60);
      if (remainingHours === 0) {
        return `${days} ${days === 1 ? 'day' : 'days'}`;
      }
      return `${days} ${days === 1 ? 'day' : 'days'} ${remainingHours} ${remainingHours === 1 ? 'hour' : 'hours'}`;
    }
  };

  const getTimeLeft = (createdAt, timeline) => {
    const createdDate = new Date(createdAt);
    const timelineMinutes = timeline;
    const endDate = new Date(createdDate.getTime() + timelineMinutes * 60000);
    const now = new Date();
    
    if (now > endDate) {
      return "Time expired";
    }
    
    const minutesLeft = Math.floor((endDate - now) / 60000);
    return formatTimeline(minutesLeft);
  };

  // Initialize Cashfree SDK
  const initializeCashfree = async () => {
    try {
      return await load({ mode: "production" });
    } catch (error) {
      console.error("Failed to initialize Cashfree:", error);
      throw error;
    }
  };

  const handlePaymentSuccess = async (userDetails) => {
    try {
      const cashfree = await initializeCashfree();

      const paymentResponse = await createPaymentSession({
        name: userDetails.name,
        email: userDetails.email,
        mobile: userDetails.mobile,
        amount: dm.amount,
        entityId: dm._id,
        entityType: "priorityDM",
      });

      const { order_id, payment_session_id } = paymentResponse.data;

      await cashfree.checkout({
        paymentSessionId: payment_session_id,
        redirectTarget: "_modal",
      });

      const verificationResponse = await verifyPayment({
        orderId: order_id,
        entityId: dm._id,
        entityType: "priorityDM",
      });

      if (verificationResponse.data.status === "SUCCESS") {
        // Submit the question after successful payment
        await onAsk(question);
        setShowPaymentModal(false);
        setShowQuestionForm(false);
        setQuestion("");
        toast.success("Payment successful and question submitted!");
      } else {
        toast.error("Payment verification failed");
      }
    } catch (error) {
      console.error("Payment error:", error);
      toast.error("Payment process failed");
    } finally {
      setShowPaymentModal(false);
    }
  };

  const handleSubmitQuestion = async (e) => {
    e.preventDefault();
    
    // If the DM is paid and not free, show payment modal
    if (dm.amount > 0) {
      setShowPaymentModal(true);
      return;
    }

    // Existing free question submission logic
    try {
      setIsAskLoading(true);
      const response = await onAsk(question);
      let newQuestion;
      
      if (!response) {
        newQuestion = {
          _id: Date.now().toString(),
          userId: {
            _id: loggedInUserId,
          },
          question: question,
          createdAt: new Date().toISOString(),
          isAnswered: false
        };
      } else {
        // Transform the response to match our expected structure
        newQuestion = {
          ...response,
          userId: {
            _id: response.userId
          }
        };
      }
      
     

      // Create new arrays instead of modifying existing ones
      const updatedUserQuestions = [...userQuestions, newQuestion];
      const updatedDmQuestions = dm.questions ? [...dm.questions, newQuestion] : [newQuestion];

      // Update both states
      setUserQuestions(updatedUserQuestions);
      dm.questions = updatedDmQuestions;
      
      setQuestion("");
      setShowQuestionForm(false);
      
      // Force show the button by updating state
      if (updatedUserQuestions.length > 0) {
        setShowUserQuestionsPopup(false);
      }
    } catch (error) {
      console.error("Error submitting question:", error);
      toast.error("Failed to submit question");
    } finally {
      setIsAskLoading(false);
    }
  };

  const handleViewQuestions = async () => {
    try {
      setIsLoading(true);
      const response = await getPriorityDMByIdAPI(communityId, dm._id);
      setQuestions(response.data.questions || []);
      setShowQuestions(true);
    } catch (error) {
      console.error("Error fetching questions:", error);
      toast.error("Failed to load questions");
    } finally {
      setIsLoading(false);
    }
  };

  const handleOpenSocialShare = (e) => {
    e.stopPropagation();
    const baseUrl = window.location.origin;
    const dmUrl = `${baseUrl}/community/${communityId}/priority-dm/${dm._id}`;
    setSocialUrl(dmUrl);
    setSharePopupOpen(true);
  };

  const handleCardClick = (e) => {
    // Check if the click target is not a button or interactive element
    const isInteractiveElement = e.target.closest('.action-section, .admin-actions, .card-actions, button, textarea');
    if (!isInteractiveElement) {
      navigate(`/community/${communityId}/priority-dm/${dm._id}`);
    }
  };

  const truncateDescription = (htmlString) => {
    // Create a temporary div to parse HTML
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = htmlString;
    const textContent = tempDiv.textContent || tempDiv.innerText;
    
    // Truncate to 150 characters and add ellipsis if longer
    return textContent.length > 150 
      ? textContent.substring(0, 150) + '...'
      : textContent;
  };

  return (
    <>
      <div className="service-card priority-dm-card" onClick={handleCardClick}>
        <div className="service-content">
          <h3>{dm.title}</h3>
          <p className="truncated-description">
            {truncateDescription(dm.description)}
          </p>
          <div className="service-details">
            <span>Response Time: {formatTimeline(dm.timeline)}</span>
            <span className={`badge ${dm.amount === 0 ? "free" : "paid"}`}>
              {dm.amount === 0 ? "Free" : `₹${dm.amount}`}
            </span>
          </div>
          <div className="topics">
            {dm.topics.map((topic, index) => (
              <span key={index} className="topic-tag">
                {topic}
              </span>
            ))}
          </div>
        </div>

        {!isAdmin && (
          <div className="action-section" onClick={(e) => e.stopPropagation()}>
            <div className="button-group" style={{ display: 'flex', gap: '1rem', marginBottom: '1rem' }}>
              <button
                className="ask-button"
                onClick={() => setShowQuestionForm(!showQuestionForm)}
              >
                Ask Question
              </button>
              {userQuestions.length > 0 && (
                <button
                  className="view-questions-button"
                  onClick={() => setShowUserQuestionsPopup(true)}
                >
                  View Your Questions ({userQuestions.length})
                </button>
              )}
            </div>

            {showQuestionForm && (
              <form onSubmit={handleSubmitQuestion}>
                <textarea
                  value={question}
                  onChange={(e) => { e.stopPropagation(); setQuestion(e.target.value); }}
                  placeholder="Type your question here..."
                  disabled={isAskLoading}
                />
                <button type="submit" disabled={isAskLoading}>
                  {isAskLoading ? "Submitting..." : "Submit"}
                </button>
              </form>
            )}
          </div>
        )}

        {isAdmin && (
          <div className="admin-actions">
            <button 
              className="view-questions-button"
              onClick={handleViewQuestions}
              disabled={isLoading}
              style={{ minWidth: "120px" }}
            >
              {isLoading ? "Loading..." : "View Questions"}
              {!isLoading && dm.questions?.filter(q => !q.isAnswered).length > 0 && (
                <span className="question-badge">
                  {dm.questions.filter(q => !q.isAnswered).length}
                </span>
              )}
            </button>
            <button className="edit-button" onClick={(e) => { e.stopPropagation(); onUpdate(dm); }}>
              <FaPen size={20} />
            </button>
            <button className="delete-button" onClick={(e) => { 
              e.stopPropagation(); 
              if (window.confirm('Are you sure you want to delete this Priority DM?')) {
                onDelete();
              }
            }}>
              <FaTrashCan size={20} />
            </button>
          </div>
        )}

        <div className="card-actions">
          <button className="share-button" onClick={handleOpenSocialShare}>
            <FaShare size={20} />
          </button>
        </div>
      </div>

      {showUserQuestionsPopup && (
        <div className="modal-overlay">
          <div className="modal-content priority-dm-modal">
            <div className="modal-header">
              <h3>Your Questions</h3>
              <button 
                className="close-button"
                onClick={(e) => { e.stopPropagation(); setShowUserQuestionsPopup(false); }}
              >
                ×
              </button>
            </div>
            <div className="modal-body">
              {userQuestions.map((q, index) => (
                <div key={index} className="question-item">
                  <div className="question-header">
                    <p className="question-text"><strong>Question:</strong> {q.question}</p>
                    <span className={`status-badge ${q.isAnswered ? 'answered' : 'pending'}`}>
                      {q.isAnswered ? 'Answered' : `Time left: ${getTimeLeft(q.createdAt, dm.timeline)}`}
                    </span>
                  </div>
                  {q.isAnswered && (
                    <p className="answer-text">
                      <strong>Answer:</strong> {q.answer}
                    </p>
                  )}
                  <small className="timestamp">
                    Asked {formatDistanceToNow(new Date(q.createdAt))} ago
                  </small>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {showQuestions && (
        <QuestionsPopup
          questions={questions}
          timeline={dm.timeline}
          onClose={() => setShowQuestions(false)}
          communityId={communityId}
          dmId={dm._id}
        />
      )}

      {showPaymentModal && (
        <PurchasePopup
          isOpen={showPaymentModal}
          onClose={() => setShowPaymentModal(false)}
          itemDetails={{
            type: "priorityDM",
            name: dm.title,
            description: truncateDescription(dm.description),
            amount: dm.amount,
            is_free: dm.amount === 0,
          }}
          loggedInUser={loggedInUser}
          onProceed={handlePaymentSuccess}
        />
      )}

      {sharePopupOpen && (
        <SharePopup
          postId={dm._id}
          isOpen={sharePopupOpen}
          setIsOpen={setSharePopupOpen}
          url={socialUrl}
        />
      )}
    </>
  );
};

export default PriorityDMCard; 